import { type ReactNode, useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight, Check, Sparkles, Loader2 } from "lucide-react";
import { MarketingLayout } from "@/components/marketing/Layout";
import {
  useAutoTranslate,
  collectBlockStrings,
  applyBlockStrings,
} from "@/components/AutoTranslate";

// Shared, polished renderer for blog articles. One consistent, responsive,
// SEO-rich layout so every post looks intentional instead of ad-hoc — with
// a key-takeaways box, structured sections, CTA, FAQ and related links.

export type Block =
  | { type: "p"; text: ReactNode }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: ReactNode[] }
  | { type: "ol"; items: ReactNode[] }
  | { type: "callout"; title?: string; text: ReactNode }
  | { type: "quote"; text: string; cite?: string }
  | { type: "table"; head: string[]; rows: ReactNode[][] };

export type Faq = { q: string; a: string };
export type Related = { slug: string; title: string };

export type Article = {
  path: string; // e.g. "/blog/what-is-an-ai-twin"
  category: string;
  readTime: string; // e.g. "8 min read"
  published: string; // ISO date
  updated?: string;
  title: string;
  titleEm?: string;
  /** On-page opening paragraph. Free to be long — it is copy, not metadata. */
  lead: string;
  /**
   * Meta description, kept under ~160 characters.
   *
   * Several posts used `lead` for this and ran to 270 characters, so Google
   * truncated roughly a third of every snippet. The two jobs are different: the
   * lead sets up an article, the description earns a click from a result list.
   */
  metaDescription?: string;
  takeaways?: string[];
  blocks: Block[];
  faqs?: Faq[];
  related?: Related[];
  cta?: { title: string; text: string };
};

const SITE = "https://realitytwin.io";

export function articleHead(a: Article, seo: any) {
  const scripts: any[] = [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: a.title + (a.titleEm ? ` ${a.titleEm}` : ""),
        description: a.lead,
        datePublished: a.published,
        dateModified: a.updated ?? a.published,
        author: { "@type": "Person", name: "Ajdin Ruznic" },
        publisher: { "@type": "Organization", name: "Reality Twin", url: SITE },
        mainEntityOfPage: `${SITE}${a.path}`,
      }),
    },
  ];
  if (a.faqs?.length) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: a.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    });
  }
  return { ...seo, scripts };
}

function fmtDate(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function BlockView({ b }: { b: Block }) {
  switch (b.type) {
    case "p":
      return <p className="mt-4 text-muted-foreground">{b.text}</p>;
    case "h2":
      return (
        <h2 className="mt-12 font-serif text-[28px] sm:text-[32px] leading-[1.1] tracking-[-0.02em]">
          {b.text}
        </h2>
      );
    case "h3":
      return <h3 className="mt-8 font-serif text-[22px] leading-tight">{b.text}</h3>;
    case "ul":
      return (
        <ul className="mt-4 space-y-2.5 text-muted-foreground">
          {b.items.map((it, i) => (
            <li key={i} className="flex gap-3">
              <Check className="mt-1.5 h-3.5 w-3.5 shrink-0 text-foreground" strokeWidth={2.5} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="mt-4 space-y-3 list-decimal pl-5 text-muted-foreground marker:text-muted-foreground/60">
          {b.items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ol>
      );
    case "callout":
      return (
        <div className="mt-6 rounded-xl hairline bg-cream p-6">
          {b.title && (
            <div className="flex items-center gap-2 font-mono-eyebrow">
              <Sparkles className="h-3.5 w-3.5" /> {b.title}
            </div>
          )}
          <div
            className={`text-[15px] leading-relaxed text-foreground/85 ${b.title ? "mt-3" : ""}`}
          >
            {b.text}
          </div>
        </div>
      );
    case "quote":
      return (
        <blockquote className="mt-8 border-l-2 border-foreground pl-5">
          <p className="font-serif text-[22px] leading-[1.3] tracking-[-0.01em] text-foreground/90">
            "{b.text}"
          </p>
          {b.cite && <div className="mt-2 text-[13px] text-muted-foreground">— {b.cite}</div>}
        </blockquote>
      );
    case "table":
      return (
        <div className="mt-6 hairline rounded-xl overflow-x-auto">
          <table className="w-full text-[14px] min-w-[520px]">
            <thead>
              <tr className="bg-cream text-[13px] font-medium">
                {b.head.map((h, i) => (
                  <th key={i} className={`p-4 text-left ${i > 0 ? "border-l border-border" : ""}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, ri) => (
                <tr key={ri} className="border-t border-border">
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={`p-4 align-top ${ci > 0 ? "border-l border-border text-muted-foreground" : "font-medium"}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

const CTA_TITLE_FALLBACK = "Turn your website into an AI Twin that sells.";
const CTA_TEXT_FALLBACK =
  "Reality Twin is live. Create your account and we'll spin up a Twin from your public site in about 60 seconds — free to start.";

export function BlogArticle({ article }: { article: Article }) {
  const a = article;

  // Auto-translate the whole article into the reader's active language.
  const chrome = [
    "Key takeaways",
    "FAQ",
    "Questions people ask",
    "Get started free",
    "See pricing",
    "Keep reading",
    a.category,
    a.readTime,
  ];
  const head = [a.title, a.titleEm ?? "", a.lead];
  const takeaways = a.takeaways ?? [];
  const ctaPair = [a.cta?.title ?? CTA_TITLE_FALLBACK, a.cta?.text ?? CTA_TEXT_FALLBACK];
  const faqPairs = (a.faqs ?? []).flatMap((f) => [f.q, f.a]);
  const related = (a.related ?? []).map((r) => r.title);
  const blockStrings = useMemo(() => collectBlockStrings(a.blocks as any), [a.blocks]);
  const strings = useMemo(
    () => [...chrome, ...head, ...takeaways, ...ctaPair, ...faqPairs, ...related, ...blockStrings],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [a],
  );
  const { out, busy } = useAutoTranslate(strings);
  let cur = 0;
  const C = chrome.map(() => out[cur++] ?? "");
  const T = { title: out[cur++] ?? a.title, titleEm: out[cur++], lead: out[cur++] ?? a.lead };
  const takeawaysT = takeaways.map(() => out[cur++]);
  const ctaT = { title: out[cur++], text: out[cur++] };
  const faqsT = (a.faqs ?? []).map((f) => ({ q: out[cur++] ?? f.q, a: out[cur++] ?? f.a }));
  const relatedT = (a.related ?? []).map((r) => ({ slug: r.slug, title: out[cur++] ?? r.title }));
  const blocksT = applyBlockStrings(a.blocks as any, out.slice(cur)) as Block[];

  return (
    <MarketingLayout>
      <article className="py-14 sm:py-20">
        {/* Header */}
        <div className="mx-auto max-w-3xl px-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
            <Link to="/blog" className="font-mono uppercase tracking-wide hover:text-foreground">
              {C[6]}
            </Link>
            <span className="text-border">·</span>
            <span>{C[7]}</span>
            <span className="text-border">·</span>
            <time dateTime={a.published}>{fmtDate(a.published)}</time>
            {busy && <Loader2 className="h-3 w-3 animate-spin" />}
          </div>
          <h1 className="mt-5 font-serif text-[40px] sm:text-[60px] leading-[1.02] tracking-[-0.03em]">
            {T.title} {T.titleEm && <em className="italic text-muted-foreground">{T.titleEm}</em>}
          </h1>
          <p className="mt-6 text-[18px] leading-[1.6] text-muted-foreground">{T.lead}</p>
          <p className="mt-4 text-[13px] text-muted-foreground">by Ajdin Ruznic · Reality Twin</p>
        </div>

        {/* Key takeaways */}
        {a.takeaways?.length ? (
          <div className="mx-auto max-w-3xl px-6 mt-10">
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">
              <div className="font-mono-eyebrow">{C[0]}</div>
              <ul className="mt-4 space-y-2.5">
                {a.takeaways.map((t, i) => (
                  <li key={i} className="flex gap-3 text-[14.5px] text-foreground/85">
                    <Check
                      className="mt-1 h-3.5 w-3.5 shrink-0 text-foreground"
                      strokeWidth={2.5}
                    />
                    <span>{takeawaysT[i] ?? t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        {/* Body */}
        <div className="mx-auto max-w-3xl px-6 mt-6 text-[16.5px] leading-[1.75] text-foreground/90">
          {blocksT.map((b, i) => (
            <BlockView key={i} b={b} />
          ))}

          {/* CTA */}
          <section className="mt-14 hairline rounded-2xl bg-cream p-8 sm:p-10">
            <div className="font-mono-eyebrow">Reality Twin</div>
            <h3 className="mt-3 font-serif text-[26px] sm:text-[30px] leading-[1.1] tracking-[-0.02em]">
              {ctaT.title}
            </h3>
            <p className="mt-3 text-[15px] text-muted-foreground">{ctaT.text}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/auth"
                className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2.5 text-[13.5px] font-medium text-background hover:opacity-90"
              >
                {C[3]} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center rounded-full hairline bg-background px-4 py-2.5 text-[13.5px] hover:bg-card"
              >
                {C[4]}
              </Link>
            </div>
          </section>

          {/* FAQ */}
          {a.faqs?.length ? (
            <section className="mt-14">
              <div className="font-mono-eyebrow">{C[1]}</div>
              <h2 className="mt-3 font-serif text-[28px] leading-[1.1] tracking-[-0.02em]">
                {C[2]}
              </h2>
              <div className="mt-6 space-y-4">
                {faqsT.map((f) => (
                  <div key={f.q} className="hairline rounded-xl bg-card p-6">
                    <h3 className="font-serif text-[19px] leading-tight">{f.q}</h3>
                    <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {/* Related */}
          {a.related?.length ? (
            <section className="mt-14">
              <div className="font-mono-eyebrow">{C[5]}</div>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {relatedT.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}` as string}
                    className="group hairline rounded-xl bg-card p-5 hover:bg-cream transition flex items-start justify-between gap-3"
                  >
                    <span className="font-serif text-[17px] leading-snug">{r.title}</span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
                  </Link>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </article>
    </MarketingLayout>
  );
}
